import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { LayoutGrid, X, ChevronDown, ChevronLeft, ChevronRight, Play, Check, SkipBack, SkipForward, ExternalLink } from 'lucide-react';
import api from '../../services/api';
import logoImg from '../../assets/logo.png';
import templeImg from '../../assets/temple-far1.jpg';
import CommentSection from '../CommentSection/CommentSection';
import PageMeta from '../SEO/PageMeta';
import './StudySyllabus.css';

/* ── Subjects (order = order of the side panel and the home shelves) ─────── */
const SUBJECT_META = {
  'History':                 { gs: 'GS-I' },
  'Geography':               { gs: 'GS-I' },
  'Art & Culture':           { gs: 'GS-I' },
  'Polity':                  { gs: 'GS-II' },
  'International Relations': { gs: 'GS-II' },
  'Social Issues':           { gs: 'GS-II' },
  'Governance':              { gs: 'GS-II' },
  'Economy':                 { gs: 'GS-III' },
  'Environment':             { gs: 'GS-III' },
  'Science & Technology':    { gs: 'GS-III' },
  'Disaster Management':     { gs: 'GS-III' },
  'Ethics':                  { gs: 'GS-IV' },
  'Current Affairs':         { gs: 'All GS' },
};
const FALLBACK_META = { gs: '' };

/* ── Helpers ─────────────────────────────────────────────────────────────── */
const BASE = '/study-upsc-syllabus';
const enc = encodeURIComponent;
const subjectPath = s => `${BASE}/${enc(s)}`;
const watchPath = (s, c, id) => `${subjectPath(s)}/${enc(c)}${id ? `?v=${id}` : ''}`;
const loggedIn = () => !!localStorage.getItem('vidyacards_token');
const thumb = (id, q = 'mqdefault') => `https://img.youtube.com/vi/${id}/${q}.jpg`;

function getYouTubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:v=|youtu\.be\/|embed\/)([A-Za-z0-9_-]{11})/);
  return match ? match[1] : null;
}

/* ── Data layer: same three endpoints as before, cached for the session ──── */
const cache = { counts: null, chapters: new Map(), videos: new Map() };
const memo = (map, key, load) => {
  if (!map.has(key)) map.set(key, load().catch(err => { console.error(err); map.delete(key); return []; }));
  return map.get(key);
};
const getCounts = () => cache.counts || (cache.counts = api.get('/syllabus/counts')
  .then(r => r.data.counts || {})
  .catch(err => { console.error(err); cache.counts = null; return {}; }));
const getChapters = s => memo(cache.chapters, s,
  () => api.get('/syllabus/chapters', { params: { subject: s } }).then(r => r.data.chapters || []));
const getVideos = (s, c) => memo(cache.videos, `${s}\u0001${c}`,
  () => api.get('/syllabus/videos', { params: { subject: s, chapter: c } }).then(r => r.data.videos || []));

/* First videos of a subject, in chapter order — feeds a home-page shelf */
async function getShelf(subject, max = 14) {
  const chapters = await getChapters(subject);
  const out = [];
  for (const ch of chapters) {
    if (out.length >= max) break;
    if (ch.videoCount === 0) continue;
    const vids = await getVideos(subject, ch.name);
    vids.forEach(v => out.push({ ...v, subject, chapter: ch.name }));
  }
  return out.slice(0, max);
}

function useData(fn, deps, initial) {
  const [data, setData] = useState(initial);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let live = true;
    setLoading(true);
    fn().then(d => { if (live) { setData(d); setLoading(false); } });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return [data, loading];
}

function useInView(ref, eager) {
  const [seen, setSeen] = useState(!!eager);
  useEffect(() => {
    if (seen) return undefined;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setSeen(true); return undefined; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setSeen(true); io.disconnect(); } }, { rootMargin: '300px' });
    io.observe(el);
    return () => io.disconnect();
  }, [seen, ref]);
  return seen;
}

/* ── Left panel: subjects, each with a chapter dropdown ──────────────────── */
function ChapterNav({ subject, chapter, onPick }) {
  const [chs, loading] = useData(() => getChapters(subject), [subject], []);
  if (loading) return <p className="sv-note">Loading…</p>;
  if (!chs.length) return <p className="sv-note">Coming soon</p>;
  return (
    <div className="sv-chs">
      {chs.map(c => (
        <Link key={c.name} to={watchPath(subject, c.name)} title={c.name} onClick={onPick}
          aria-current={c.name === chapter ? 'true' : undefined}
          className={`sv-chl ${c.name === chapter ? 'on' : ''}`}>{c.name}</Link>
      ))}
    </div>
  );
}

function SidePanel({ subject, chapter, slim, open, onClose }) {
  const [expanded, setExpanded] = useState(() => new Set(subject ? [subject] : []));
  useEffect(() => {
    if (subject) setExpanded(p => (p.has(subject) ? p : new Set(p).add(subject)));
  }, [subject]);
  const toggle = s => setExpanded(p => { const n = new Set(p); if (n.has(s)) n.delete(s); else n.add(s); return n; });
  const authed = loggedIn();

  return (
    <aside className={`sv-side ${slim ? 'slim' : ''} ${open ? 'open' : ''}`} aria-label="Subjects">
      <img className="sv-side-art" src={templeImg} alt="" />
      <div className="sv-side-body">
        <div className="sv-side-top">
          <Link to={authed ? '/dashboard' : '/'} className="sv-logo" aria-label="CivilFox home"><img src={logoImg} alt="" /></Link>
          <button type="button" className="sv-x" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <nav className="sv-cats">
          <Link to={BASE} onClick={onClose} className={`sv-cat sv-cat-name ${!subject ? 'on' : ''}`}>
            <i className="sv-dot" />All
          </Link>
          {Object.keys(SUBJECT_META).map(s => {
            const isOpen = expanded.has(s);
            return (
              <div key={s} className={`sv-grp ${isOpen ? 'open' : ''}`}>
                <div className={`sv-cat ${s === subject && !chapter ? 'on' : ''}`}>
                  <Link to={subjectPath(s)} onClick={onClose} className="sv-cat-name"
                    aria-current={s === subject && !chapter ? 'true' : undefined}>
                    <i className="sv-dot" />{s}
                  </Link>
                  <button type="button" className="sv-caret" onClick={() => toggle(s)}
                    aria-expanded={isOpen} aria-label={`Chapters in ${s}`}>
                    <ChevronDown size={15} />
                  </button>
                </div>
                {isOpen && <ChapterNav subject={s} chapter={s === subject ? chapter : null} onPick={onClose} />}
              </div>
            );
          })}
        </nav>
        <Link className="sv-side-foot" to={authed ? '/dashboard' : '/register'} onClick={onClose}>
          {authed ? 'Dashboard' : 'Start free'}
        </Link>
      </div>
    </aside>
  );
}

/* ── Shell: panel stays mounted while the view beside it changes ─────────── */
function Shell({ subject, chapter, children }) {
  const [navOpen, setNavOpen] = useState(false);
  const slim = !!(subject && chapter);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = [html.style.overflow, body.style.overflow];
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => { html.style.overflow = prev[0]; body.style.overflow = prev[1]; };
  }, []);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') setNavOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const close = () => setNavOpen(false);
  return (
    <div className={`sv ${slim ? 'is-player' : ''}`}>
      <SidePanel subject={subject} chapter={chapter} slim={slim} open={navOpen} onClose={close} />
      <div className="sv-stage">
        <div className="sv-mbar">
          <button type="button" className="sv-grid-btn" onClick={() => setNavOpen(true)} aria-label="All subjects"><LayoutGrid size={18} /></button>
          <span>{chapter || subject || 'Video syllabus'}</span>
        </div>
        {children}
      </div>
      <div className={`sv-backdrop ${navOpen ? 'show' : ''}`} onClick={close} />
    </div>
  );
}

/* ── Shelves (Netflix / Apple TV+ rows) ──────────────────────────────────── */
function VideoCard({ v, label }) {
  const id = getYouTubeId(v.youtubeUrl);
  return (
    <Link className="sv-card" to={watchPath(v.subject, v.chapter, v._id)}>
      <span className="sv-thumb">
        {id ? <img src={thumb(id)} alt="" loading="lazy" /> : <i className="sv-thumb-ph" />}
        {v.duration && <em className="sv-dur">{v.duration}</em>}
        <span className="sv-thumb-play"><Play size={18} fill="currentColor" /></span>
      </span>
      <b className="sv-card-title">{v.title}</b>
      {label && <small className="sv-card-sub">{label}</small>}
    </Link>
  );
}

function Shelf({ title, sub, to, toLabel, load, onLoad, eager, chapterLabels }) {
  const box = useRef(null);
  const track = useRef(null);
  const seen = useInView(box, eager);
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!seen) return undefined;
    let live = true;
    load().then(v => { if (!live) return; setItems(v); if (onLoad) onLoad(v); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seen]);

  if (items && !items.length) return null;
  const scroll = dir => track.current?.scrollBy({ left: dir * track.current.clientWidth * 0.85, behavior: 'smooth' });

  return (
    <section className="sv-shelf" ref={box}>
      <header className="sv-shelf-head">
        <h2>{title}</h2>
        {sub && <span>{sub}</span>}
        {to && <Link to={to}>{toLabel}</Link>}
      </header>
      <div className="sv-row">
        <button type="button" className="sv-arrow l" onClick={() => scroll(-1)} aria-label="Scroll left"><ChevronLeft size={20} /></button>
        <div className="sv-track" ref={track}>
          {items
            ? items.map((v, i) => <VideoCard key={v._id || i} v={v} label={chapterLabels ? v.chapter : `Lecture ${i + 1}`} />)
            : [...Array(5)].map((_, i) => <div key={i} className="sv-card"><div className="sv-thumb sv-skel" /></div>)}
        </div>
        <button type="button" className="sv-arrow r" onClick={() => scroll(1)} aria-label="Scroll right"><ChevronRight size={20} /></button>
      </div>
    </section>
  );
}

/* ── ALL: hero + one shelf per subject ───────────────────────────────────── */
function Hero({ v }) {
  const id = getYouTubeId(v.youtubeUrl);
  return (
    <section className="sv-hero">
      {id && (
        <img className="sv-hero-img" src={thumb(id)} alt=""
          onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = thumb(id, 'hqdefault'); }} />
      )}
      <div className="sv-hero-copy">
        <span className="sv-hero-tag">{v.subject}</span>
        <h2>{v.title}</h2>
        {v.description && <p>{v.description}</p>}
        <Link className="sv-hero-btn" to={watchPath(v.subject, v.chapter, v._id)}>
          <Play size={15} fill="currentColor" />Watch now
        </Link>
      </div>
    </section>
  );
}

function HomeView() {
  const [counts, loading] = useData(getCounts, [], null);
  const [hero, setHero] = useState(null);
  const subjects = Object.keys(SUBJECT_META).filter(s => counts && counts[s] > 0);

  return (
    <div className="sv-scroll">
      <PageMeta
        title="UPSC Video Syllabus: Free Subject-wise Lectures"
        description="Browse a structured UPSC Civil Services video syllabus with subject-wise and chapter-wise lectures for General Studies preparation."
        path={BASE}
      />
      <h1 className="sv-sr">UPSC Video Syllabus</h1>
      {(loading || subjects.length > 0) && (hero ? <Hero v={hero} /> : <div className="sv-hero sv-skel" />)}
      {subjects.map((s, i) => (
        <Shelf key={s} eager={i < 2} title={s} sub={SUBJECT_META[s].gs}
          to={subjectPath(s)} toLabel="See all" chapterLabels
          load={() => getShelf(s)}
          onLoad={v => { if (v[0]) setHero(h => h || v[0]); }} />
      ))}
      {!loading && subjects.length === 0 && (
        <div className="sv-empty"><h3>Videos coming soon</h3><p>We are uploading lectures subject by subject. Check back shortly.</p></div>
      )}
    </div>
  );
}

/* ── SUBJECT: one shelf per chapter ──────────────────────────────────────── */
function SubjectView({ subject }) {
  const meta = SUBJECT_META[subject] || FALLBACK_META;
  const [chapters, loading] = useData(() => getChapters(subject), [subject], []);
  const chs = chapters.filter(c => c.videoCount !== 0);

  return (
    <div className="sv-scroll">
      <PageMeta
        title={`${subject} UPSC Syllabus: Chapters & Video Lectures`}
        description={`Explore ${subject} in the UPSC syllabus with chapter-wise video lectures and study resources.`}
        path={subjectPath(subject)}
        robots={!loading && chs.length ? 'index, follow' : 'noindex, follow'}
      />
      <header className="sv-head">
        <h1>{subject}</h1>
        {meta.gs && <span className="sv-gs">{meta.gs}</span>}
      </header>
      {loading ? (
        <section className="sv-shelf"><div className="sv-track">{[...Array(5)].map((_, i) => <div key={i} className="sv-card"><div className="sv-thumb sv-skel" /></div>)}</div></section>
      ) : chs.length === 0 ? (
        <div className="sv-empty"><h3>Chapters coming soon</h3><p>We are building the syllabus for this subject.</p></div>
      ) : chs.map((c, i) => (
        <Shelf key={subject + c.name} eager={i < 2} title={c.name}
          sub={c.videoCount ? `${c.videoCount} videos` : ''}
          to={watchPath(subject, c.name)} toLabel="Play chapter"
          load={() => getVideos(subject, c.name).then(vs => vs.map(v => ({ ...v, subject, chapter: c.name })))} />
      ))}
    </div>
  );
}

/* ── PLAYER: video + comments, playlist of the whole subject on the right ── */
function PlayerView({ subject, chapter }) {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const vId = sp.get('v');
  const meta = SUBJECT_META[subject] || FALLBACK_META;

  const [chapters] = useData(() => getChapters(subject), [subject], []);
  const chs = useMemo(() => chapters.filter(c => c.videoCount !== 0), [chapters]);
  const [vids, setVids] = useState({});                       // chapter name -> videos
  const [open, setOpen] = useState(() => new Set([chapter]));  // expanded chapters in the playlist
  const wKey = `cf_watched_${subject}`;
  const [watched, setWatched] = useState(() => {
    try { return JSON.parse(localStorage.getItem(wKey) || '[]'); } catch { return []; }
  });
  const playerRef = useRef(null);
  const underRef = useRef(null);
  const listRef = useRef(null);

  const ensure = useCallback(name => {
    getVideos(subject, name).then(v => setVids(p => (p[name] ? p : { ...p, [name]: v })));
  }, [subject]);

  useEffect(() => {
    ensure(chapter);
    setOpen(p => (p.has(chapter) ? p : new Set(p).add(chapter)));
  }, [chapter, ensure]);

  // Preload the neighbouring chapters so "next" and "previous" run on across chapters
  const pi = chs.findIndex(c => c.name === chapter);
  const prevCh = pi > 0 ? chs[pi - 1] : null;
  const nextCh = pi > -1 ? chs[pi + 1] || null : null;
  useEffect(() => {
    if (prevCh) ensure(prevCh.name);
    if (nextCh) ensure(nextCh.name);
  }, [prevCh, nextCh, ensure]);

  const videos = vids[chapter];
  const activeIdx = videos ? Math.max(0, videos.findIndex(v => v._id === vId)) : -1;
  const active = videos ? videos[activeIdx] : null;
  const activeId = active ? active._id : null;
  const ytId = active ? getYouTubeId(active.youtubeUrl) : null;

  /* Watched progress, stored by stable video id */
  const mark = useCallback(id => { if (id) setWatched(p => (p.includes(id) ? p : [...p, id])); }, []);
  useEffect(() => {
    try { localStorage.setItem(wKey, JSON.stringify(watched)); } catch { /* storage unavailable */ }
  }, [watched, wKey]);
  useEffect(() => {   // carry over progress saved under the old per-chapter keys
    try {
      const old = JSON.parse(localStorage.getItem(`cf_watched_${subject}_${chapter}`) || '[]');
      if (old.length) setWatched(p => [...new Set([...p, ...old])]);
    } catch { /* ignore */ }
  }, [subject, chapter]);
  const watchedCount = videos ? videos.filter(v => watched.includes(v._id)).length : 0;

  /* Previous / next, crossing chapter boundaries */
  const last = arr => arr[arr.length - 1];
  const prevVid = activeIdx > 0
    ? { c: chapter, v: videos[activeIdx - 1] }
    : prevCh && vids[prevCh.name]?.length ? { c: prevCh.name, v: last(vids[prevCh.name]) } : null;
  const nextVid = videos && activeIdx < videos.length - 1
    ? { c: chapter, v: videos[activeIdx + 1] }
    : nextCh && vids[nextCh.name]?.length ? { c: nextCh.name, v: vids[nextCh.name][0] } : null;
  const go = t => { if (t) navigate(watchPath(subject, t.c, t.v._id)); };

  useEffect(() => {
    [underRef, playerRef].forEach(r => r.current?.scrollTo?.(0, 0));
  }, [activeId]);
  useEffect(() => {
    if (window.innerWidth > 1000) listRef.current?.querySelector('.sv-it.on')?.scrollIntoView({ block: 'nearest' });
  }, [activeId, chapter]);

  const toggleCh = name => {
    if (!open.has(name)) ensure(name);
    setOpen(p => { const n = new Set(p); if (n.has(name)) n.delete(name); else n.add(name); return n; });
  };

  return (
    <div className="sv-player" ref={playerRef}>
      <PageMeta
        title={`${chapter} ${subject} UPSC Video Lectures`}
        description={active?.description || `Watch ${chapter} video lectures for ${subject} in the UPSC syllabus.`}
        path={watchPath(subject, chapter)}
        robots={videos && videos.length ? 'index, follow' : 'noindex, follow'}
      />

      <main className="sv-center">
        {!videos ? (
          <>
            <div className="sv-stick"><div className="sv-embed sv-skel" /></div>
            <div className="sv-under" ref={underRef}><div className="sv-skel sv-line" /></div>
          </>
        ) : !active ? (
          <div className="sv-empty">
            <h3>Videos coming soon</h3>
            <p>We are uploading lectures for this chapter. Check back shortly.</p>
            <Link className="sv-btn" to={subjectPath(subject)}>Back to {subject}</Link>
          </div>
        ) : (
          <>
            <div className="sv-stick">
              <div className="sv-embed">
                {ytId ? (
                  <iframe key={ytId}
                    src={`https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0&modestbranding=1`}
                    title={active.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                    allowFullScreen onLoad={() => mark(active._id)} />
                ) : <p className="sv-embed-none">Video not available yet</p>}
              </div>
            </div>

            <div className="sv-under" ref={underRef}>
              <div className="sv-info">
                <div className="sv-info-main">
                  <h1>{active.title}</h1>
                  <div className="sv-meta">
                    <span className="sv-chip">{subject}{meta.gs ? ` ${meta.gs}` : ''}</span>
                    <span className="sv-chip">{chapter}</span>
                    <span className="sv-chip">Lecture {activeIdx + 1} of {videos.length}</span>
                    {active.duration && <span className="sv-chip">{active.duration}</span>}
                  </div>
                </div>
                <div className="sv-acts">
                  <button type="button" className="sv-iconbtn" onClick={() => go(prevVid)} disabled={!prevVid} aria-label="Previous video" title="Previous"><SkipBack size={17} /></button>
                  <button type="button" className="sv-iconbtn" onClick={() => go(nextVid)} disabled={!nextVid} aria-label="Next video" title="Next"><SkipForward size={17} /></button>
                  {ytId && (
                    <a className="sv-iconbtn" href={`https://www.youtube.com/watch?v=${ytId}`} target="_blank" rel="noopener noreferrer"
                      aria-label="Watch on YouTube" title="Watch on YouTube"><ExternalLink size={17} /></a>
                  )}
                </div>
              </div>

              {active.description && <p className="sv-desc">{active.description}</p>}

              {active._id && (
                <div className="sv-comments">
                  <CommentSection cardId={active._id} isLoggedIn={true} />
                </div>
              )}
            </div>
          </>
        )}
      </main>

      <aside className="sv-list" ref={listRef} aria-label="Playlist">
        <div className="sv-list-head">
          <h2>{subject}</h2>
          {videos && videos.length > 0 && <span>{watchedCount}/{videos.length} watched in this chapter</span>}
          <div className="sv-bar"><i style={{ width: videos && videos.length ? `${(watchedCount / videos.length) * 100}%` : 0 }} /></div>
        </div>
        {chs.map(c => {
          const isOpen = open.has(c.name);
          const list = vids[c.name];
          return (
            <section key={c.name} className={`sv-ch ${c.name === chapter ? 'cur' : ''} ${isOpen ? 'open' : ''}`}>
              <button type="button" className="sv-ch-head" onClick={() => toggleCh(c.name)} aria-expanded={isOpen}>
                <ChevronDown size={15} />
                <span>{c.name}</span>
                <small>{c.videoCount ?? (list ? list.length : '')}</small>
              </button>
              {isOpen && (list ? list.map((v, i) => {
                const id = getYouTubeId(v.youtubeUrl);
                const on = c.name === chapter && v._id === activeId;
                return (
                  <Link key={v._id || i} to={watchPath(subject, c.name, v._id)} aria-current={on ? 'true' : undefined}
                    className={`sv-it ${on ? 'on' : ''}`}>
                    <span className="sv-it-thumb">
                      {id ? <img src={thumb(id)} alt="" loading="lazy" /> : <i className="sv-thumb-ph" />}
                      {on && <span className="sv-now"><Play size={16} fill="currentColor" /></span>}
                      {!on && watched.includes(v._id) && <span className="sv-seen"><Check size={11} strokeWidth={3} /></span>}
                      {v.duration && <em className="sv-dur">{v.duration}</em>}
                    </span>
                    <span className="sv-it-body">
                      <b className="sv-it-title">{v.title}</b>
                      {v.description && <span className="sv-it-desc">{v.description}</span>}
                    </span>
                  </Link>
                );
              }) : <p className="sv-note">Loading…</p>)}
            </section>
          );
        })}
      </aside>
    </div>
  );
}

/* ── Root ─────────────────────────────────────────────────────────────────── */
export default function StudySyllabus() {
  const { subject: sp, chapter: cp } = useParams();
  const subject = sp ? decodeURIComponent(sp) : null;
  const chapter = cp ? decodeURIComponent(cp) : null;
  return (
    <Shell subject={subject} chapter={chapter}>
      {subject && chapter ? <PlayerView key={subject} subject={subject} chapter={chapter} />
        : subject ? <SubjectView subject={subject} />
        : <HomeView />}
    </Shell>
  );
}